import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { investorApi } from '../../api/portal';
import usePortalQuery, { invalidatePortal } from '../../hooks/usePortalQuery';
import { QueryStatus } from '../../components/portal/PortalUI';

const empty = { name: '', email: '', phone: '', notes: '', status: 'active' };
const editable = ['name', 'email', 'phone', 'notes', 'status'];
export const investorPatch = (original, form) => {
  const patch = Object.fromEntries(editable.filter((field) => form[field] !== (original[field] ?? '')).map((field) => [field, form[field]]));
  if (patch.email) patch.email = patch.email.trim().toLowerCase();
  return patch;
};
export const investorCreatePayload = (form) => ({
  ...Object.fromEntries(editable.map((field) => [field, form[field]])),
  email: form.email.trim().toLowerCase(),
});
export const isGmailAddress = (email) => /^[^\s@]+@gmail\.com$/i.test(email.trim());

function InvestorForm({ investor, onSaved }) {
  const [form, setForm] = useState(() => ({ ...empty, ...investor }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!isGmailAddress(form.email || '')) {
      setError('Enter a valid Gmail address ending in @gmail.com.');
      setMessage('');
      return;
    }
    const payload = investor ? investorPatch(investor, form) : investorCreatePayload(form);
    if (!Object.keys(payload).length) { setMessage('No changes to save.'); return; }
    setBusy(true); setError(''); setMessage('');
    try {
      const saved = investor ? await investorApi.update(investor.id, payload) : await investorApi.create(payload);
      setForm({ ...empty, ...saved });
      setMessage(investor ? 'Investor account updated.' : 'Account created. The investor can sign in with Google using this Gmail address.');
      invalidatePortal();
      onSaved?.(saved);
    } catch (requestError) {
      setError(requestError.message);
    } finally { setBusy(false); }
  };
  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value });
  return <section className="portal-card"><h2>{investor ? 'Edit account' : 'Create investor account'}</h2>
    {error && <p role="alert" className="portal-error">{error}</p>}{message && <p role="status" className="portal-success">{message}</p>}
    <form className="portal-form" onSubmit={submit}>
      <label>Name<input required value={form.name} onChange={update('name')} /></label>
      <label>Gmail address<input type="email" required value={form.email || ''} onChange={update('email')} autoComplete="email" /><span className="portal-muted">The investor uses Google sign-in with this @gmail.com address.</span></label>
      <label>Phone<input type="tel" value={form.phone || ''} onChange={update('phone')} /></label>
      <label>Account status<select value={form.status} onChange={update('status')}><option value="active">Active</option><option value="inactive">Inactive (deactivated)</option></select></label>
      <label>Administrative notes<textarea value={form.notes || ''} onChange={update('notes')} /></label>
      <div className="portal-actions"><button disabled={busy}>{busy ? 'Saving...' : 'Save account'}</button></div>
    </form>
    <p className="portal-muted">Deactivate accounts instead of deleting them to retain ownership and financial references.</p>
  </section>;
}

export default function Investors() {
  const { investorId } = useParams();
  return investorId ? <InvestorDetail key={investorId} investorId={investorId} /> : <InvestorList />;
}
function InvestorList() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || '';
  const [search, setSearch] = useState('');
  const [create, setCreate] = useState(false);
  const [message, setMessage] = useState('');
  const [sort, setSort] = useState('name');
  const query = usePortalQuery(['investors', status], (signal) => investorApi.list(status, signal));
  const rows = query.data?.filter((item) => `${item.name} ${item.email || ''}`.toLowerCase().includes(search.toLowerCase())).slice().sort((a, b) => String(a[sort] || '').localeCompare(String(b[sort] || '')));
  return <main className="portal"><header className="portal-header"><h1>Investors</h1><p>Manage accounts and view investor portfolios.</p><Link to="/admin/vehicles">Manage vehicle ownership</Link></header>
    <div className="portal-controls"><label>Status<select value={status} onChange={(event) => setParams({ status: event.target.value })}><option value="">All accounts</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label><label>Search investors<input value={search} onChange={(event) => setSearch(event.target.value)} /></label><label>Sort by<select value={sort} onChange={(event) => setSort(event.target.value)}><option value="name">Name</option><option value="email">Gmail address</option><option value="status">Status</option></select></label><button onClick={() => setCreate(!create)}>{create ? 'Close create form' : 'New investor'}</button></div>
    {message && <p className="portal-success" role="status">{message}</p>}
    {create && <InvestorForm onSaved={(saved) => { setCreate(false); setMessage(`${saved.name}'s account was created. They can sign in with Google using their Gmail address.`); }} />}
    <QueryStatus query={query} />
    {query.data && <section className="portal-card">{rows.length ? <div className="portal-table-scroll"><table><caption>Investor accounts</caption><thead><tr><th>Name</th><th>Gmail address</th><th>Status</th></tr></thead><tbody>{rows.map((item) => <tr key={item.id}><td><Link to={`/admin/investors/${encodeURIComponent(item.id)}`}>{item.name}</Link></td><td>{item.email || 'Not provided'}</td><td>{item.status}</td></tr>)}</tbody></table></div> : <p>No investors match the selected filters.</p>}</section>}
  </main>;
}
function InvestorDetail({ investorId }) {
  const query = usePortalQuery(['investor', investorId], (signal) => investorApi.get(investorId, signal));
  const vehicles = usePortalQuery(['investor-vehicles', investorId], (signal) => investorApi.vehicles(investorId, signal));
  const [editingRecord, setEditingRecord] = useState(null);
  const [message, setMessage] = useState('');
  useEffect(() => { if (query.data && !editingRecord) setEditingRecord(query.data); }, [query.data, editingRecord]);
  return <main className="portal"><header className="portal-header"><h1>Investor account</h1><nav><Link to="/admin/investors">Back to investors</Link><Link to={`/owner?investor_id=${encodeURIComponent(investorId)}`}>View reported cars and totals</Link><Link to={`/admin/finance?investor_id=${encodeURIComponent(investorId)}`}>Upload investor report</Link></nav></header>
    <QueryStatus query={query} />
    {message && <p className="portal-success" role="status">{message}</p>}
    {editingRecord && <InvestorForm key={JSON.stringify(editingRecord)} investor={editingRecord} onSaved={(saved) => { setEditingRecord(saved); setMessage('Investor account updated.'); }} />}
    <p className="portal-muted">Reported cars come from the uploaded Summary. Imports create missing profiles and assign unowned cars; conflicting owners must be resolved in Car management.</p>
    <section className="portal-card"><h2>Currently owned cars</h2><QueryStatus query={vehicles} />
      {vehicles.data && (vehicles.data.length ? <ul>{vehicles.data.map((id) => <li key={id}><Link to={`/admin/vehicles/${encodeURIComponent(id)}`}>{id}</Link></li>)}</ul> : <p>No assigned cars.</p>)}
      <p className="portal-muted">All manual costs are charged to the current owner. Reassignment does not rewrite imported statements.</p>
    </section>
  </main>;
}
